/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { saveAs } from "file-saver";
import {
  Activity,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  Loader2,
  PencilLine,
  RotateCcw,
  Search,
  Users,
} from "lucide-react";
import axiosInstance from "../services/Axios/axios";
import PageSkeleton from "./PageSkeleton";

/**
 * Human labels for AUDIT_ACTION. Kept in step with the enum in the backend's
 * src/audit/schemas/audit-log.schema.ts — an unknown action falls back to its
 * raw key rather than rendering blank.
 */
export const AuditActionLabels = {
  FUND_UPLOAD: "Fund Upload",
  FUND_STAFF_UPLOAD: "Fund Upload (Staff)",
  LOAN_UPLOAD: "Loan Upload",
  LOAN_STAFF_UPLOAD: "Loan Upload (Staff)",
  MART_UPLOAD: "MART Upload",
  MART_STAFF_UPLOAD: "MART Upload (Staff)",
  MEMBER_UPLOAD: "Member Upload",
  FUND_DATE_UPDATE: "Fund Date Update",
  LOAN_DATE_UPDATE: "Loan Date Update",
  MART_DATE_UPDATE: "MART Date Update",
  MEMBER_PASSWORD_RESET: "Member Password Reset",
  FUND_ANALYSIS_RUN: "Contribution Analysis Run",
  LOAN_ANALYSIS_RUN: "Deduction Analysis Run",
  ANALYSIS_EXPORT: "Analysis Export",
  // Member & system-user administration
  MEMBER_USER_TYPE_CHANGE: "Member User Type Changed",
  MEMBER_BULK_DELETE: "Members Bulk Deleted",
  MEMBER_NURSES_DELETE: "Nurse Records Deleted",
  SYSTEM_USER_CREATE: "System User Created",
  SYSTEM_USER_EDIT: "System User Edited",
  SYSTEM_USER_DELETE: "System User Deleted",
  // Loan officer daily work
  LOAN_REBOOK: "Loan Rebooked",
  LOAN_STATUS_SUBMITTED: "Loan Submitted",
  LOAN_STATUS_BOOKED: "Loan Booked",
  LOAN_STATUS_INITIATED: "Loan Initiated",
  LOAN_STATUS_AUTHORIZED: "Loan Authorized",
  LOAN_STATUS_PAID: "Loan Marked Paid",
  LOAN_STATUS_REJECTED: "Loan Rejected",
  LOAN_REJECTED_BY_HEAD: "Loan Rejected (Head)",
  LOAN_REJECTED_BY_ADMIN: "Loan Rejected (Admin)",
  LOAN_PERIOD_DELETE: "Loan Period Deleted",
  // MART officer daily work
  ORDER_STATUS_INITIATED: "Order Initiated",
  ORDER_STATUS_SHIPPED: "Order Shipped",
  ORDER_STATUS_APPROVED: "Order Approved",
  ORDER_STATUS_REJECTED: "Order Rejected",
  ORDER_REVERSED: "Order Reversed",
  ORDER_REQUEST_MESSAGE: "Order Request Reply",
  // Customer service
  TICKET_CREATE: "Ticket Opened",
  TICKET_UPDATE: "Ticket Edited",
  TICKET_REPLY: "Ticket Reply",
  TICKET_STATUS_CHANGE: "Ticket Status Changed",
  TICKET_ASSIGN: "Ticket Assigned",
  TICKET_ESCALATE: "Ticket Escalated",
  TICKET_PRIORITY_CHANGE: "Ticket Priority Changed",
};

const actionLabel = (a) => AuditActionLabels[a] || a || "—";

/** AUDIT_CATEGORY → what the officers actually call it. */
const CategoryLabels = {
  FUNDS: "Funds",
  LOANS: "Loans",
  MART: "MART Deductions",
  ORDERS: "Mart Orders",
  MEMBERS: "Members",
  SUPPORT: "Customer Service",
  SYSTEM: "System Users",
};

const categoryLabel = (c) =>
  CategoryLabels[c] || (c ? c.charAt(0) + c.slice(1).toLowerCase() : "—");

const CATEGORY_STYLES = {
  FUNDS: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  LOANS: "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
  MART: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  ORDERS: "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  MEMBERS: "bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
  SUPPORT: "bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
  SYSTEM: "bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
};

/** Local calendar day as YYYY-MM-DD, which is what the date inputs take. */
const dayStamp = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() - offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

// "What did the officers do today" is the question this page exists to answer,
// so the ranges are presets rather than something to assemble by hand.
const RANGE_PRESETS = [
  { key: "today", label: "Today", from: () => dayStamp(0) },
  { key: "7d", label: "Last 7 days", from: () => dayStamp(6) },
  { key: "30d", label: "Last 30 days", from: () => dayStamp(29) },
  { key: "all", label: "All time", from: () => "" },
];

const TILES = [
  { key: "writes", label: "Changes", icon: <PencilLine size={15} />, accent: "emerald", hint: "Uploads, date corrections, resets" },
  { key: "reads", label: "Views", icon: <Eye size={15} />, accent: "violet", hint: "Analysis runs and exports" },
  { key: "failures", label: "Failures", icon: <AlertTriangle size={15} />, accent: "rose", hint: "Operations that threw an error" },
  { key: "actors", label: "Officers", icon: <Users size={15} />, accent: "amber", hint: "Distinct people in this view" },
];

const accentClasses = {
  emerald: { card: "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800", icon: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400", num: "text-emerald-700 dark:text-emerald-300" },
  amber: { card: "bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800", icon: "bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400", num: "text-amber-700 dark:text-amber-300" },
  rose: { card: "bg-rose-50 dark:bg-rose-900/20 border-rose-100 dark:border-rose-800", icon: "bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400", num: "text-rose-700 dark:text-rose-300" },
  violet: { card: "bg-violet-50 dark:bg-violet-900/20 border-violet-100 dark:border-violet-800", icon: "bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400", num: "text-violet-700 dark:text-violet-300" },
};

const EMPTY_FILTERS = {
  search: "",
  action: "",
  category: "",
  kind: "WRITE", // changes first; views are one click away
  status: "",
  actor: "",
  from: "",
  to: "",
};

const PAGE_SIZE = 25;
/** Matches MAX_UNPAGINATED in the backend audit service. */
const EXPORT_CAP = 5000;

const fmtWhen = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function StatusBadge({ status }) {
  const ok = status !== "FAILURE";
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${ok
        ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
        : "bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
        }`}
    >
      {ok ? "Success" : "Failed"}
    </span>
  );
}

/**
 * The master audit trail: every fund / loan / MART / member data operation and
 * every analysis run, newest first. No page layout of its own — mounted inside
 * both the Admin and IT Officer shells, which see the same unfiltered trail.
 *
 * Props:
 *  accent - button / focus-ring colour key ("cyan" | "indigo").
 */
function AuditTrailPanel({ accent = "cyan" }) {
  const ring = accent === "indigo" ? "focus:ring-indigo-500" : "focus:ring-cyan-500";
  const btn = accent === "indigo" ? "bg-indigo-600 hover:bg-indigo-700" : "bg-cyan-600 hover:bg-cyan-700";
  const exportBtn = accent === "indigo"
    ? "text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
    : "text-cyan-700 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/20 hover:bg-cyan-100 dark:hover:bg-cyan-900/40";
  // No horizontal padding here: the search field needs room for its icon, and
  // relying on `pl-8` to beat a `px-3` in the same string only works by
  // Tailwind's stylesheet order. Each field states its own x-padding instead.
  const inputBase = `py-2 rounded-xl border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-slate-800 dark:text-slate-200 text-xs focus:ring-2 ${ring} focus:border-transparent outline-none transition-all`;

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  // Only the search box is debounced; the selects apply immediately.
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [envelope, setEnvelope] = useState(null);
  const [summary, setSummary] = useState(null);
  const [options, setOptions] = useState({ actions: [], categories: [], actors: [] });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.search === searchInput ? f : { ...f, search: searchInput }));
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Only non-empty filters are sent, so the backend never sees `action=""`.
  const activeParams = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) p[k] = v;
    });
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, stats] = await Promise.all([
        axiosInstance.get("/audit", {
          params: { ...activeParams, isPaginated: true, page, size: PAGE_SIZE },
        }),
        axiosInstance.get("/audit/summary", { params: activeParams }),
      ]);
      const data = list.data;
      setRows(Array.isArray(data) ? data : data?.docs ?? []);
      setEnvelope(Array.isArray(data) ? null : data);
      setSummary(stats.data);
    } catch (e) {
      toast.error(e?.response?.data?.message ?? "Could not load the audit trail");
      setRows([]);
      setEnvelope(null);
    } finally {
      setLoading(false);
    }
  }, [activeParams, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Dropdowns are built from what the trail actually contains.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get("/audit/filters");
        setOptions({
          actions: data?.actions ?? [],
          categories: data?.categories ?? [],
          actors: data?.actors ?? [],
        });
      } catch (e) {
        // Non-fatal: the table still works, the selects are just empty.
      }
    })();
  }, []);

  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  /** A preset only moves `from`; `to` stays open so today is always included. */
  const applyRange = (preset) => {
    setFilters((f) => ({ ...f, from: preset.from(), to: "" }));
    setPage(1);
  };

  const activeRange = useMemo(() => {
    if (!filters.from && !filters.to) return "all";
    const match = RANGE_PRESETS.find(
      (p) => p.key !== "all" && !filters.to && p.from() === filters.from
    );
    return match?.key ?? null; // null = a hand-picked range
  }, [filters.from, filters.to]);

  const reset = () => {
    setSearchInput("");
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  /** Exports everything matching the current filters, not just this page. */
  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data } = await axiosInstance.get("/audit", {
        params: { ...activeParams, isPaginated: false, size: EXPORT_CAP },
      });
      const all = Array.isArray(data) ? data : data?.docs ?? [];
      if (!all.length) {
        toast.info("Nothing to export");
        return;
      }
      const headers = [
        "createdAt", "actor_name", "actor_email", "actor_super_role", "action",
        "category", "kind", "status", "description", "period", "member_type",
        "file_name", "record_count", "target_staff_id", "target_ref",
        "error_message", "client_asserted", "ip",
      ];
      const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const csv = [
        headers.join(","),
        ...all.map((r) => headers.map((h) => escape(r[h])).join(",")),
      ].join("\n");
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
      saveAs(blob, `audit_trail_${new Date().toISOString().slice(0, 10)}.csv`);
      if (all.length >= EXPORT_CAP) {
        toast.info(`Export capped at ${EXPORT_CAP.toLocaleString()} rows — narrow the date range for more`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message ?? "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = envelope?.totalPages ?? 1;
  const totalItems = envelope?.totalItems ?? rows.length;

  return (
    <>
      {/* <p className="text-sm text-slate-500 dark:text-slate-400 -mt-2 mb-6">
        Staff activity across the whole system: deduction uploads and corrections,
        member account resets and system-user changes, loan officers' work on
        applications, MART officers' work on orders, customer-service ticket handling,
        and every analysis run or export — who did it, when, from where, and whether it
        succeeded. Entries are kept permanently; the view starts on{" "}
        <span className="font-semibold">Changes</span>, with analysis{" "}
        <span className="font-semibold">Views</span> one click away.
      </p> */}

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {TILES.map((t) => {
          const c = accentClasses[t.accent];
          const count = summary?.[t.key] ?? 0;
          return (
            <div key={t.key} className={`p-5 rounded-2xl border ${c.card}`}>
              <div className="flex items-start justify-between mb-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.icon}`}>{t.icon}</div>
                <span className={`text-2xl font-black ${c.num}`}>{Number(count).toLocaleString()}</span>
              </div>
              <p className="text-sm font-bold text-slate-800 dark:text-slate-100">{t.label}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t.hint}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm p-5 mb-6">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => applyRange(p)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${activeRange === p.key
                ? `text-white ${btn}`
                : "text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-gray-700/50 hover:bg-slate-100 dark:hover:bg-gray-700"
                }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative lg:col-span-2">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search member, description, period, staff ID, file"
              // pl-9 clears the icon (left-3 + 14px wide); pr-8 leaves room for
              // the native clear button a type="search" input draws on the right.
              className={`${inputBase} pl-9 pr-8 w-full`}
            />
          </div>
          <select value={filters.kind} onChange={(e) => setFilter("kind", e.target.value)} className={`${inputBase} px-3 w-full`}>
            <option value="WRITE">Activity: Changes</option>
            <option value="READ">Activity: Views</option>
            <option value="">Activity: All</option>
          </select>
          <select value={filters.category} onChange={(e) => setFilter("category", e.target.value)} className={`${inputBase} px-3 w-full`}>
            <option value="">All categories</option>
            {options.categories.map((c) => (
              <option key={c} value={c}>{categoryLabel(c)}</option>
            ))}
          </select>
          <select value={filters.action} onChange={(e) => setFilter("action", e.target.value)} className={`${inputBase} px-3 w-full`}>
            <option value="">All actions</option>
            {options.actions.map((a) => (
              <option key={a} value={a}>{actionLabel(a)}</option>
            ))}
          </select>
          <select value={filters.actor} onChange={(e) => setFilter("actor", e.target.value)} className={`${inputBase} px-3 w-full`}>
            <option value="">All officers</option>
            {options.actors.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <select value={filters.status} onChange={(e) => setFilter("status", e.target.value)} className={`${inputBase} px-3 w-full`}>
            <option value="">Any outcome</option>
            <option value="SUCCESS">Successful only</option>
            <option value="FAILURE">Failures only</option>
          </select>
          <div className="flex items-center gap-2">
            <input
              type="date" value={filters.from} onChange={(e) => setFilter("from", e.target.value)}
              className={`${inputBase} px-3 w-full`} aria-label="From date"
            />
            <span className="text-xs text-slate-400">to</span>
            <input
              type="date" value={filters.to} onChange={(e) => setFilter("to", e.target.value)}
              className={`${inputBase} px-3 w-full`} aria-label="To date"
            />
          </div>
        </div>
      </div>

      {/* Trail */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Audit Trail</h3>
            <span className="text-xs text-slate-400">({Number(totalItems).toLocaleString()})</span>
            {loading && <Loader2 size={14} className="animate-spin text-slate-400" />}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={reset}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-gray-700/50 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
            >
              <RotateCcw size={14} /> Reset filters
            </button>
            <button
              onClick={exportCsv}
              disabled={exporting}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold ${exportBtn} transition-colors whitespace-nowrap disabled:opacity-50`}
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Export CSV
            </button>
          </div>
        </div>

        {loading && !rows.length ? (
          <div className="p-5"><PageSkeleton cards={0} cols={6} /></div>
        ) : (
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0">
                <tr className="bg-slate-50 dark:bg-gray-700/50">
                  {["When", "Officer", "Action", "What happened", "Period / Record", "Records", "Outcome"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                {rows.length > 0 ? (
                  rows.map((r) => (
                    <tr key={r._id} className="hover:bg-slate-50 dark:hover:bg-gray-700/30 transition-colors">
                      <td className="px-4 py-2.5 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">{fmtWhen(r.createdAt)}</td>
                      <td className="px-4 py-2.5 text-sm">
                        <div className="font-semibold text-slate-700 dark:text-slate-300">
                          {r.actor_name || <span className="text-slate-400 italic">Unknown</span>}
                        </div>
                        <div className="text-xs text-slate-400">{r.actor_email || "—"}</div>
                      </td>
                      <td className="px-4 py-2.5 text-sm whitespace-nowrap">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${CATEGORY_STYLES[r.category] || "bg-slate-100 text-slate-500 dark:bg-gray-700 dark:text-slate-400"}`}>
                          {actionLabel(r.action)}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300">
                        {r.description || "—"}
                        {r.file_name && (
                          <div className="text-xs text-slate-400">{r.file_name}</div>
                        )}
                        {r.status === "FAILURE" && r.error_message && (
                          <div className="text-xs text-rose-600 dark:text-rose-400">{r.error_message}</div>
                        )}
                        {r.client_asserted && (
                          <div className="text-xs text-slate-400 italic">reported by the browser</div>
                        )}
                      </td>
                      {/* One column for whatever the action was aimed at: a
                          deduction period, a member, or a loan/order/ticket. */}
                      <td className="px-4 py-2.5 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                        {r.period || r.target_staff_id || r.target_ref || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-slate-600 dark:text-slate-400">
                        {typeof r.record_count === "number" ? r.record_count.toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-sm"><StatusBadge status={r.status} /></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-400 dark:text-slate-500">
                      <Activity size={28} className="mx-auto text-slate-300 dark:text-slate-600 mb-2" />
                      No audit entries match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-100 dark:border-gray-700">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-gray-700/50 hover:bg-slate-100 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft size={14} /> Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold text-white ${btn} disabled:opacity-40 transition-colors`}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      <ToastContainer autoClose={3000} />
    </>
  );
}

export default AuditTrailPanel;
